import { ChangeDetectionStrategy, AfterViewInit, ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { StateService } from '@uirouter/core';
import { SpacesService } from 'account-hybrid/common/components/spaces';
import { IdentityService } from 'account-hybrid/core/authentication';
import { GuestsApiService, GuestsQuery } from '../services/guests-api.service';

interface DirectoryFilter {
  name: string;
  query: GuestsQuery;
}

/**
 * Native-Angular port of the AngularJS people DirectoryController
 * (account-v1/.../people/directory/directory.controller.js) + directory.html.
 * Replaces the `people.directory` UIRouter state's AngularJS component.
 */
@Component({
    selector: 'ft-people-directory-ng',
    templateUrl: './directory.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DirectoryComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scrollSentinel') scrollSentinel?: ElementRef<HTMLElement>;

  readonly filters: DirectoryFilter[] = [
    { name: 'Most recent', query: { SortBy: 'create_date', SortDirection: 'desc', Offset: 0, Fetch: 25 } },
    { name: 'Top advocates', query: { SortBy: '', SortDirection: 'desc', Offset: 0, Fetch: 25 } },
  ];
  filter: DirectoryFilter = this.clone(this.filters[0]);
  advocates: any[] = [];
  criteria = '';
  searchText = '';
  hasMoreData = false;
  isProcessingRequest = false;
  /**
   * Read ONCE, in ngOnInit, rather than called from the template through `identity`: PII permission
   * derives from the session's per-space Permissions, which no in-page session write touches, and
   * the current space, read off the URL's space segment: `people` declares `type`/`slug` dynamic,
   * but no in-app transition changes them (a space switch is a full reload). A field is also what
   * lets the CD audit see the whole of what this view renders (FTK-7983).
   */
  hasPiiPermissions = false;

  private queryToken = 0;
  private observer?: IntersectionObserver;
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly guests: GuestsApiService,
    private readonly identity: IdentityService,
    private readonly spaces: SpacesService,
    private readonly stateService: StateService,
  ) {}

  ngOnInit(): void {
    this.hasPiiPermissions = this.identity.hasPiiPermissions();
    this.changeFilter(this.filters[0]);
  }

  ngAfterViewInit(): void {
    if (!this.scrollSentinel) return;
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && this.hasMoreData && !this.isProcessingRequest) {
        this.loadNextPage();
      }
    });
    this.observer.observe(this.scrollSentinel.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  /** Total count comes back on the first advocate row (matches the v1 controller). */
  get total(): number {
    return this.advocates.length > 0 ? this.advocates[0].totalCount : 0;
  }

  changeFilter(newFilter: DirectoryFilter): void {
    this.filter = this.clone(newFilter);
    this.advocates = [];
    this.load((data) => {
      this.advocates = Array.isArray(data.advocates) ? data.advocates : [];
    });
  }

  setCriteria(criteria: string): void {
    this.searchText = criteria;
    this.filter.query.Offset = 0;
    this.advocates = [];
    this.load((data) => {
      this.advocates = Array.isArray(data.advocates) ? data.advocates : [];
    });
  }

  loadNextPage(): void {
    this.filter.query.Offset += this.filter.query.Fetch;
    this.load((data) => {
      this.advocates = this.advocates.concat(data.advocates ?? []);
    });
  }

  details(guest: any): void {
    this.stateService.go('people.directory.details', { advocateUUID: guest.campaignPromoterUUID });
  }

  private load(onData: (data: any) => void): void {
    const token = ++this.queryToken;
    this.isProcessingRequest = true;
    const query: GuestsQuery = { ...this.filter.query, SearchText: this.searchText };
    this.guests.guests(this.spaces.getPropertyUuid(), query).subscribe({
      next: (data) => {
        if (token !== this.queryToken) return;
        const total =
          Array.isArray(data.advocates) && data.advocates.length > 0 ? data.advocates[0].totalCount : 0;
        this.hasMoreData = total > this.filter.query.Offset + this.filter.query.Fetch;
        onData(data);
        this.cdr.markForCheck();
      },
      complete: () => {
        if (token === this.queryToken) this.isProcessingRequest = false;
        this.cdr.markForCheck();
      },
      error: () => {
        if (token === this.queryToken) this.isProcessingRequest = false;
        this.cdr.markForCheck();
      },
    });
  }

  private clone(f: DirectoryFilter): DirectoryFilter {
    return { name: f.name, query: { ...f.query } };
  }
}
